import React, { useMemo, useState } from "react";
import {
  IResourceComponentsProps,
  BaseRecord,
  useTranslate,
  useCreate,
  useUpdate,
  useInvalidate,
  useList,
} from "@refinedev/core";
import {
  useTable,
  List,
  DeleteButton,
  DateField,
} from "@refinedev/antd";
import {
  Table,
  Space,
  Tag,
  Avatar,
  Typography,
  Button,
  Modal,
  Form,
  Input,
  Row,
  Col,
  Transfer,
} from "antd";
import { GlassCard } from "../../shared/components";
import { useAvatarPalette } from "../../shared/styles";
import { getAvatarColor, getInitialsFromName } from "../../shared/utils";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import type { TransferProps } from "antd";
import { UserRole } from "../../models/user";

const { Text } = Typography;

const isClientUser = (user: { role?: { id?: number; name?: string } | null }): boolean => {
  const roleId = user.role?.id;
  return (
    roleId === UserRole.id ||
    roleId === Number(UserRole.id) ||
    user.role?.name === UserRole.name
  );
};

export const UserGroupList: React.FC<IResourceComponentsProps & { hideHeader?: boolean }> = ({
  hideHeader,
}) => {
  const _t = useTranslate();
  const avatarPalette = useAvatarPalette();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<BaseRecord | null>(null);
  const [form] = Form.useForm();

  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const invalidate = useInvalidate();

  const { tableProps, tableQueryResult } = useTable({
    syncWithLocation: true,
    resource: "user-groups",
  });

  const { data: usersData, isLoading: usersLoading } = useList({
    resource: "users",
  });

  const { mutate: createGroup, isLoading: isCreating } = useCreate();
  const { mutate: updateGroup, isLoading: isUpdating } = useUpdate();

  interface UserRecord {
    key: string;
    title: string;
    email: string;
    firstName: string;
    lastName: string;
    roleName: string;
    serviceProviderName: string;
  }

  const dataSource = useMemo<UserRecord[]>(() => {
    return (usersData?.data || [])
      .filter((user: any) => !isClientUser(user))
      .map((user: any) => ({
        key: String(user.id),
        title: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
        email: user.email,
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        roleName: user.role?.name || "",
        serviceProviderName: user.serviceProviderName || "",
      }));
  }, [usersData]);

  const handleOpenCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setTargetKeys([]);
    setSelectedKeys([]);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (record: BaseRecord) => {
    setEditingRecord(record);
    const users = ((record as any).users || []).filter(
      (user: any) => !isClientUser(user),
    );
    const initialKeys = users.map((user: any) => String(user.id));
    setTargetKeys(initialKeys);
    setSelectedKeys([]);
    form.setFieldsValue({
      name: (record as any).name,
      comments: (record as any).comments,
      userIds: users.map((user: any) => user.id),
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
    form.resetFields();
    setTargetKeys([]);
    setSelectedKeys([]);
  };

  const onChange: TransferProps["onChange"] = (nextTargetKeys) => {
    setTargetKeys(nextTargetKeys as string[]);
    form.setFieldsValue({
      userIds: nextTargetKeys.map((key) => Number(key)),
    });
  };

  const onSelectChange: TransferProps["onSelectChange"] = (
    sourceSelectedKeys,
    targetSelectedKeys,
  ) => {
    setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys] as string[]);
  };

  const filterOption = (inputValue: string, option: UserRecord) => {
    const searchValue = inputValue.toLowerCase();
    return (
      option.email.toLowerCase().includes(searchValue) ||
      option.firstName.toLowerCase().includes(searchValue) ||
      option.lastName.toLowerCase().includes(searchValue)
    );
  };

  const handleFormFinish = (values: any) => {
    const payload = {
      name: values.name,
      comments: values.comments,
      userIds: targetKeys.map((key) => Number(key)),
    };

    if (editingRecord) {
      updateGroup(
        {
          resource: "user-groups",
          id: editingRecord.id,
          values: payload,
        },
        {
          onSuccess: () => {
            handleCloseModal();
            invalidate({ resource: "user-groups", invalidates: ["list"] });
            tableQueryResult?.refetch();
          },
        },
      );
    } else {
      createGroup(
        {
          resource: "user-groups",
          values: payload,
        },
        {
          onSuccess: () => {
            handleCloseModal();
            invalidate({ resource: "user-groups", invalidates: ["list"] });
            tableQueryResult?.refetch();
          },
        },
      );
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <List
        resource="user-groups"
        title={hideHeader ? false : _t("user-groups.titles.list", "User Groups")}
        breadcrumb={hideHeader ? false : undefined}
        headerButtons={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenCreateModal}
          >
            {_t("buttons.create", "Create")}
          </Button>
        }
      >
        <style>{`
                .user-groups-list-table-view .ant-table { background: transparent !important; }
                .user-groups-list-table-view .ant-table-thead > tr > th { background: transparent !important; }
                .user-groups-list-table-view .ant-table-tbody > tr > td { background: transparent !important; }
            `}</style>
        <GlassCard className="user-groups-list-table-view !p-0 overflow-hidden">
          <Table {...tableProps} rowKey="id">
            <Table.Column
              title={_t("user-groups.fields.name", "Group")}
              key="group"
              width={300}
              render={(_, record: any) => (
                <Space>
                  <Avatar
                    shape="square"
                    size={44}
                    style={{
                      backgroundColor: getAvatarColor(record.id, avatarPalette),
                      fontWeight: 600,
                    }}
                  >
                    {getInitialsFromName(record.name)}
                  </Avatar>
                  <div>
                    <Text strong>{record.name}</Text>
                    {record.comments && (
                      <>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {record.comments}
                        </Text>
                      </>
                    )}
                    <br />
                    <Tag color="blue" style={{ marginTop: 4 }}>
                      {record.users?.length || 0}{" "}
                      {record.users?.length === 1
                        ? _t("user-groups.fields.users")?.slice(0, -1)
                        : _t("user-groups.fields.users")}
                    </Tag>
                  </div>
                </Space>
              )}
            />
            <Table.Column
              dataIndex="comments"
              title={_t("user-groups.fields.comments", "Description")}
              render={(value: string) => value || "-"}
            />
            <Table.Column
              dataIndex="users"
              title={_t("user-groups.fields.usersCount", "Number of users")}
              render={(users: any[]) => (
                <Tag color="blue">{users?.length || 0}</Tag>
              )}
            />
            <Table.Column
              dataIndex="createdAt"
              title={_t("user-groups.fields.createdAt", "Created At")}
              render={(value: string) => (
                <DateField value={value} format="DD/MM/YYYY HH:mm" />
              )}
            />
            <Table.Column
              title={_t("table.actions", "Actions")}
              dataIndex="actions"
              render={(_, record: BaseRecord) => (
                <Space>
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => handleOpenEditModal(record)}
                  />
                  <DeleteButton
                    resource="user-groups"
                    hideText
                    size="small"
                    recordItemId={record.id}
                  />
                </Space>
              )}
            />
          </Table>
        </GlassCard>
      </List>
      <Modal
        title={
          editingRecord
            ? _t("user-groups.titles.edit", "Edit Group")
            : _t("user-groups.titles.create", "Create Group")
        }
        open={isModalOpen}
        onCancel={handleCloseModal}
        width={700}
        footer={[
          <Button key="cancel" onClick={handleCloseModal}>
            {_t("buttons.cancel", "Cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isCreating || isUpdating}
            onClick={() => form.submit()}
          >
            {_t("buttons.save", "Save")}
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormFinish}
        >
          <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
            <Col xs={24}>
              <Form.Item
                label={_t("user-groups.fields.name", "Name")}
                name="name"
                rules={[
                  {
                    required: true,
                    message: _t(
                      "forms.errors.required",
                      "This field is required",
                    ),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
            <Col xs={24}>
              <Form.Item
                label={_t("user-groups.fields.users", "Users")}
                name="userIds"
              >
                <Transfer
                  dataSource={dataSource}
                  titles={[
                    _t("user-groups.transfer.available", "Available"),
                    _t("user-groups.transfer.selected", "Selected"),
                  ]}
                  targetKeys={targetKeys}
                  selectedKeys={selectedKeys}
                  onChange={onChange}
                  onSelectChange={onSelectChange}
                  render={(item) => (
                    <span>
                      {item.firstName} {item.lastName}{" "}
                      {item.roleName && (
                        <Tag
                          color={item.roleName === "Admin" ? "blue" : "green"}
                        >
                          {item.roleName}
                        </Tag>
                      )}
                      {item.serviceProviderName && (
                        <Tag color="orange">{item.serviceProviderName}</Tag>
                      )}
                    </span>
                  )}
                  filterOption={filterOption}
                  showSearch
                  listStyle={{
                    width: 300,
                    height: 300,
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
            <Col xs={24}>
              <Form.Item
                label={_t("user-groups.fields.comments", "Description")}
                name="comments"
              >
                <Input.TextArea rows={4} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};
